import { useState, useEffect } from 'react';
import { Event } from '../../types/Event';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { generateICSContent } from './utils';

export const useJoinModal = (initialEvent: Event, onClose: () => void, onEventUpdated: (updatedEvent: Event) => void) => {
  const [event, setEvent] = useState(initialEvent);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [spots, setSpots] = useState<number | string>(1);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showWaitingList, setShowWaitingList] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [requiresApproval, setRequiresApproval] = useState(false);
  const [isWaitlisted, setIsWaitlisted] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  
  const { isAuthenticated, user } = useAuth();
  const { user: userContext } = useUser();
  
  // Derived state
  const currentParticipantCount = event.participants?.reduce((sum, p) => sum + (p.spots ?? 0), 0) ?? 0;
  const isEventFull = currentParticipantCount >= (event.maxParticipants ?? 0);
  const spotsLeft = Math.max(0, (event.maxParticipants ?? 0) - currentParticipantCount);
  
  // Load saved user data - prioritize logged-in user data
  useEffect(() => {
    if (isAuthenticated && userContext) {
      // Logged-in user: prefill from user context
      if (userContext.participant?.name) {
        setName(userContext.participant.name);
      }
      if (userContext.phoneNumber) {
        // Strip +852 prefix for display
        const phoneWithoutPrefix = userContext.phoneNumber.replace(/^\+852/, '');
        setPhone(phoneWithoutPrefix);
      }
    } else {
      // Guest user: load from localStorage
      const savedName = localStorage.getItem('name');
      const savedPhone = localStorage.getItem('phone');
      if (savedName) setName(savedName);
      if (savedPhone) setPhone(savedPhone);
    }
  }, [isAuthenticated, userContext]);

  // Check if current user is a participant (by phone number)
  const isUserParticipant = () => {
    // Get phone number from authenticated user or localStorage
    let phoneToCheck: string | null = null;
    
    if (isAuthenticated && user?.phoneNumber) {
      // User.phoneNumber is stored as +85212345678, but participant.phone is stored as 12345678
      // So we need to strip the +852 prefix if it exists
      phoneToCheck = user.phoneNumber.replace(/^\+852/, '');
    } else {
      const savedPhone = localStorage.getItem('phone');
      if (savedPhone) {
        phoneToCheck = savedPhone;
      }
    }
    
    if (!phoneToCheck) return false;
    
    // Check in participants list
    const isInParticipants = event.participants?.some(p => p.phone === phoneToCheck) ?? false;
    // Check in waiting list
    const isInWaitingList = event.waitingList?.some(p => p.phone === phoneToCheck) ?? false;
    
    return isInParticipants || isInWaitingList;
  };

  // Input handlers
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 8);
    setPhone(value);
  };

  const handleSpotsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Allow completely empty input (important for mobile editing)
    if (value === '') {
      // On mobile, we need to allow empty state to enable proper editing
      // We'll use a special state to track this
      setSpots('' as any); // Temporarily allow empty string
      return;
    }
    
    // Parse the number
    const numValue = parseInt(value, 10);
    
    // If it's a valid number, constrain it to the range
    if (!isNaN(numValue) && numValue > 0) {
      const constrainedValue = Math.max(1, Math.min(10, numValue));
      setSpots(constrainedValue);
    } else if (value.length === 1 && /^\d$/.test(value)) {
      // Handle single digit input directly (mobile optimization)
      const singleDigit = parseInt(value, 10);
      const constrainedValue = Math.max(1, Math.min(10, singleDigit));
      setSpots(constrainedValue);
    }
  };

  // Validation
  const isValidHKPhoneNumber = (phoneNumber: string) => {
    const hkPhoneRegex = /^([2-9][0-9]{7})$/;
    return hkPhoneRegex.test(phoneNumber);
  };

  const isFormValid = () => {
    const numSpots = typeof spots === 'string' ? parseInt(spots, 10) : spots;
    return name.trim() !== '' && isValidHKPhoneNumber(phone) && !isNaN(numSpots) && numSpots >= 1 && numSpots <= 10;
  };

  // Form submission
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!isFormValid()) {
      setError('請填寫所有必填欄位並確保資料正確');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const numSpots = typeof spots === 'string' ? parseInt(spots, 10) || 1 : spots;
      console.log('發送加入請求:', { name, phone, spots: numSpots });
      const response = await axios.post(`${API_BASE_URL}/public/events/${event._id}/join`, { name, phone, spots: numSpots });
      
      // Check if approval is required
      setRequiresApproval(response.data.requiresApproval || false);
      // Check if user was added to waitlist
      setIsWaitlisted(response.data.isWaitlisted || false);

      // Save name and phone to localStorage
      localStorage.setItem('name', name);
      localStorage.setItem('phone', phone);

      // Fetch the updated event data
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      }
      
      let updatedEvent;
      if (isAuthenticated) {
        const updatedEventResponse = await axios.get(`${API_BASE_URL}/events/${event._id}`, { headers });
        updatedEvent = updatedEventResponse.data;
      } else {
        const updatedEventResponse = await axios.get(`${API_BASE_URL}/public/events/${event._id}`);
        updatedEvent = updatedEventResponse.data;
      }
      
      setEvent(updatedEvent);
      onEventUpdated(updatedEvent);
      setActiveStep(1); // Move to success step
    } catch (err) {
      console.error('錯誤回應:', err);
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || '加入活動失敗。請稍後再試。');
      } else {
        setError('發生意外錯誤。請稍後再試。');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Calendar functionality
  const addToCalendar = () => {
    // Generate iCalendar content
    const numSpots = typeof spots === 'string' ? parseInt(spots, 10) || 1 : spots;
    const icsContent = generateICSContent(event, numSpots);
    
    // Create a Blob with the iCalendar content
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    
    // Create a link element to download the file
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${(event.location ?? '') || (event.description ?? '') || '活動'}.ics`;
    
    // Trigger download
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  // Cancel enrollment functionality
  const handleCancelEnrollment = async () => {
    setError(null);
    setIsCancelling(true);

    try {
      await axios.post(
        `${API_BASE_URL}/public/events/${event._id}/cancel`,
        { phone },
        {
          headers: isAuthenticated ? {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          } : {}
        }
      );

      // Fetch the updated event data
      const headers = {
        Authorization: `Bearer ${localStorage.getItem('token')}`
      };
      
      let updatedEvent;
      if (isAuthenticated) {
        const updatedEventResponse = await axios.get(`${API_BASE_URL}/events/${event._id}`, { headers });
        updatedEvent = updatedEventResponse.data;
      } else {
        const updatedEventResponse = await axios.get(`${API_BASE_URL}/public/events/${event._id}`);
        updatedEvent = updatedEventResponse.data;
      }
      
      setEvent(updatedEvent);
      onEventUpdated(updatedEvent);
      setShowCancelConfirm(false);
      
      // Close modal after successful cancellation
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('取消報名錯誤:', err);
      if (axios.isAxiosError(err) && err.response) {
        setError(err.response.data.message || '取消報名失敗。請稍後再試。');
      } else {
        setError('發生意外錯誤。請稍後再試。');
      }
    } finally {
      setIsCancelling(false);
    }
  };

  // Return all state and handlers
  return {
    // State
    event,
    name,
    setName,
    phone,
    spots,
    error,
    isLoading,
    showParticipants,
    setShowParticipants,
    showWaitingList,
    setShowWaitingList,
    activeStep,
    requiresApproval,
    isWaitlisted,
    isAuthenticated,
    isCancelling,
    showCancelConfirm,
    setShowCancelConfirm,
    
    // Derived state
    currentParticipantCount,
    isEventFull,
    spotsLeft,
    isUserParticipant: isUserParticipant(),
    
    // Handlers
    handlePhoneChange,
    handleSpotsChange,
    handleSubmit,
    addToCalendar,
    isFormValid,
    handleCancelEnrollment,
    onClose
  };
};